/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronBack } from 'react-icons/io5';
import { MdLogout } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import type { HeaderProperties } from '@/components/Header';
import { useButtonDialogBox } from '@/hooks/useButtonDialogBox';
import useAccessLocation from '@/hooks/useGetAccessLocation';
import type { Student } from '@/slices/appSlice';
import useStore from '@/store';

const AVATAR_COLORS = ['primary-blue-3', 'success-green-3', 'primary-yellow-3'];

const avatarColor = (seed: string) => {
  let hash = 0;
  for (const char of seed) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 100_000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const nameInitials = (name: string): string => {
  const parts = name?.trim().split(' ') ?? [];
  const first = parts[0]?.[0] ?? '';
  const last = parts.at(-1)?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
};

const AVATAR_CLASSES =
  'relative z-10 -ml-[3.6875rem] h-[4.8125rem] w-[4.6875rem] shrink-0 rounded-[2.34375rem]';

interface StudentAvatarProperties {
  student: Student;
}

function StudentAvatar({ student }: StudentAvatarProperties) {
  if (student.avatar) {
    return <img src={student.avatar} alt="" className={`${AVATAR_CLASSES} object-cover`} />;
  }
  return (
    <div
      className={`${AVATAR_CLASSES} flex items-center justify-center bg-${avatarColor(
        student.id,
      )} font-lilita text-[1.75rem] font-semibold text-neutral-white`}
    >
      {nameInitials(student.name)}
    </div>
  );
}

interface StudentCardProperties {
  student: Student;
  onClick: () => void;
}

function StudentCard({ student, onClick }: StudentCardProperties) {
  const { t } = useTranslation('translation');

  return (
    <div className="relative flex h-[4.8125rem] max-w-[15.0625rem] shrink-0 items-start">
      <div className="mt-[0.625rem] flex h-[3.9375rem] items-start rounded-[1.375rem] border-2 border-neutral-white bg-neutral-white pl-[0.625rem] pr-[3.8125rem] pt-[0.1875rem]">
        <div className="flex max-w-[9.375rem] flex-col items-end pl-4">
          <p className="max-w-full truncate text-[1.25rem] font-semibold text-primary-blue-3">
            {student.name}
          </p>
          <p className="text-right text-base font-normal text-primary-blue-3">
            {`${t('grade')} ${student.grade}`}
          </p>
        </div>
      </div>
      <StudentAvatar student={student} />
      <button
        type="button"
        onClick={onClick}
        aria-label={student.name}
        className="absolute inset-0 z-20"
      />
    </div>
  );
}

function HeaderFln({
  headerTitle,
  children,
  onBack: onBackOverride,
  variant = 'default',
  backLabel,
  levelLabel,
  showBack,
  showStudentCard = true,
}: HeaderProperties): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentStudent, logoutStudent } = useStore();
  const { t } = useTranslation(['translation', 'studentLogin', 'studentProfile']);
  const { showButtonDialogBox, closeDialog } = useButtonDialogBox();
  const { accessLocation } = useAccessLocation();

  const exitRoutes = new Set([ROUTES.INSTRUCTIONS]);
  const defaultOnBack = exitRoutes.has(location.pathname)
    ? () => navigate(ROUTES.HOME, { replace: true })
    : () => navigate(-1);
  const onBack = onBackOverride ?? defaultOnBack;

  const onStudentProfileClick = () => navigate(ROUTES.STUDENT_PROFILE);

  const onStudentLogoutClick = () => {
    showButtonDialogBox({
      open: true,
      title: t('studentLogin:logoutWarning'),
      leftButtonText: t('studentLogin:cancelButton'),
      rightButtonText: t('studentProfile:logout'),
      onRightButtonClick: () => {
        navigate(
          accessLocation === 'home' ? `${ROUTES.AT_HOME}?step=profile` : ROUTES.STUDENT_LOGIN,
        );
        logoutStudent({ source: 'ui' });
      },
      onLeftButtonClick: closeDialog,
      variant: 'blue',
      showCloseButton: false,
    });
  };

  return (
    <div className="min-w-screen relative h-screen overflow-hidden">
      <header className="absolute left-0 right-0 top-0 z-elevated flex items-center px-9 py-[1.3125rem]">
        {variant === 'stage' ? (
          <button
            type="button"
            aria-label="Go Back"
            onClick={onBack}
            className="flex h-[3.875rem] shrink-0 items-center gap-3 rounded-full bg-primary-blue-3 pl-1 pr-5"
          >
            <div className="flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-full bg-neutral-white">
              <IoChevronBack style={{ fontSize: '2.0625rem', color: '#002F76' }} />
            </div>
            <span className="text-[1.5rem] font-bold text-neutral-white">{backLabel}</span>
            {levelLabel && (
              <>
                <span className="h-[2.125rem] w-[0.125rem] bg-neutral-white" />
                <span className="text-[1.5rem] font-bold text-primary-yellow-3">{levelLabel}</span>
              </>
            )}
          </button>
        ) : (
          showBack && (
            <button
              type="button"
              aria-label="Go Back"
              onClick={onBack}
              className="flex h-[3.875rem] w-[3.875rem] shrink-0 items-center justify-center rounded-full border-[4px] border-primary-blue-3 bg-neutral-white"
            >
              <IoChevronBack style={{ fontSize: '2.0625rem', color: '#002F76' }} />
            </button>
          )
        )}

        {variant === 'default' && (
          <div className="absolute left-1/2 -translate-x-1/2 px-4 py-1.5">
            <h1 className="font-outline-6 font-lilita text-[3.125rem] text-secondary-pink-1">
              {headerTitle}
            </h1>
          </div>
        )}

        <div className="ml-auto flex items-center gap-[1.3125rem]">
          {showStudentCard && currentStudent && (
            <StudentCard student={currentStudent} onClick={onStudentProfileClick} />
          )}
          <button
            type="button"
            aria-label="Logout"
            onClick={onStudentLogoutClick}
            className="flex h-[3.875rem] w-[3.875rem] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-white bg-primary-yellow-3"
          >
            <MdLogout style={{ color: '#FFFFFF', fontSize: '1.5rem', marginLeft: '0.25rem' }} />
          </button>
        </div>
      </header>

      {children}
    </div>
  );
}

export default HeaderFln;
